/**
 * WebSocketWrapper - Auth-aware WebSocket Provider Wrapper
 *
 * Only establishes WebSocket connection when user is authenticated.
 * Provides graceful degradation when WebSocket is unavailable.
 */

import { ReactNode, useMemo } from 'react';
import { WebSocketProvider } from '@teletienda/ws-client/react';
import { useAuth, getFreshAccessToken } from '@teletienda/shared-auth-ui';
import { useWsUrl } from '@/providers/PlatformBootstrapProvider';

interface WebSocketWrapperProps {
  children: ReactNode;
}

/**
 * Auth-aware WebSocket wrapper component
 *
 * - Only renders WebSocketProvider when user is authenticated
 * - Automatically passes JWT token to WebSocket connection
 * - Lets the client fetch a fresh token before each (re)connection and after an
 *   auth rejection, so the socket revives instead of staying down
 * - Gracefully degrades to no WebSocket for unauthenticated users
 *
 * @example
 * ```tsx
 * <AuthProvider apiBaseUrl={AUTH_API_URL}>
 *   <WebSocketWrapper>
 *     <App />
 *   </WebSocketWrapper>
 * </AuthProvider>
 * ```
 */
export function WebSocketWrapper({ children }: WebSocketWrapperProps): React.ReactElement {
  const { isAuthenticated, token } = useAuth();
  const WS_URL = useWsUrl();

  // Debug logging
  console.log('[WebSocketWrapper] State:', {
    isAuthenticated,
    hasToken: !!token,
    tokenLength: token?.length,
  });

  // Memoize options to prevent unnecessary reconnections
  const wsOptions = useMemo(
    () => ({
      url: WS_URL,
      token: token || '',
      getToken: getFreshAccessToken,
    }),
    [WS_URL, token]
  );

  const shouldConnect = isAuthenticated && !!token;

  console.log('[WebSocketWrapper] Rendering WebSocketProvider:', {
    autoConnect: shouldConnect,
  });

  return (
    <WebSocketProvider options={wsOptions} autoConnect={shouldConnect}>
      {children}
    </WebSocketProvider>
  );
}
